"use client";

import React, { useMemo } from "react";
import { countryNameToCode } from "@/lib/country-codes";

import AE from "country-flag-icons/react/3x2/AE";
import AR from "country-flag-icons/react/3x2/AR";
import AT from "country-flag-icons/react/3x2/AT";
import AU from "country-flag-icons/react/3x2/AU";
import BE from "country-flag-icons/react/3x2/BE";
import BR from "country-flag-icons/react/3x2/BR";
import CA from "country-flag-icons/react/3x2/CA";
import CH from "country-flag-icons/react/3x2/CH";
import CL from "country-flag-icons/react/3x2/CL";
import CN from "country-flag-icons/react/3x2/CN";
import CO from "country-flag-icons/react/3x2/CO";
import CZ from "country-flag-icons/react/3x2/CZ";
import DE from "country-flag-icons/react/3x2/DE";
import DK from "country-flag-icons/react/3x2/DK";
import ES from "country-flag-icons/react/3x2/ES";
import FI from "country-flag-icons/react/3x2/FI";
import FR from "country-flag-icons/react/3x2/FR";
import GB from "country-flag-icons/react/3x2/GB";
import GR from "country-flag-icons/react/3x2/GR";
import HK from "country-flag-icons/react/3x2/HK";
import ID from "country-flag-icons/react/3x2/ID";
import IE from "country-flag-icons/react/3x2/IE";
import IL from "country-flag-icons/react/3x2/IL";
import IN from "country-flag-icons/react/3x2/IN";
import IT from "country-flag-icons/react/3x2/IT";
import JP from "country-flag-icons/react/3x2/JP";
import KR from "country-flag-icons/react/3x2/KR";
import KW from "country-flag-icons/react/3x2/KW";
import MX from "country-flag-icons/react/3x2/MX";
import MY from "country-flag-icons/react/3x2/MY";
import NL from "country-flag-icons/react/3x2/NL";
import NO from "country-flag-icons/react/3x2/NO";
import NZ from "country-flag-icons/react/3x2/NZ";
import PH from "country-flag-icons/react/3x2/PH";
import PL from "country-flag-icons/react/3x2/PL";
import PT from "country-flag-icons/react/3x2/PT";
import QA from "country-flag-icons/react/3x2/QA";
import RU from "country-flag-icons/react/3x2/RU";
import SA from "country-flag-icons/react/3x2/SA";
import SE from "country-flag-icons/react/3x2/SE";
import SG from "country-flag-icons/react/3x2/SG";
import TH from "country-flag-icons/react/3x2/TH";
import TR from "country-flag-icons/react/3x2/TR";
import TW from "country-flag-icons/react/3x2/TW";
import UA from "country-flag-icons/react/3x2/UA";
import US from "country-flag-icons/react/3x2/US";
import VN from "country-flag-icons/react/3x2/VN";
import ZA from "country-flag-icons/react/3x2/ZA";
import BM from "country-flag-icons/react/3x2/BM";
import LU from "country-flag-icons/react/3x2/LU";
import KY from "country-flag-icons/react/3x2/KY";
import KZ from "country-flag-icons/react/3x2/KZ";
import PE from "country-flag-icons/react/3x2/PE";
import SI from "country-flag-icons/react/3x2/SI";
import MA from "country-flag-icons/react/3x2/MA";
import CY from "country-flag-icons/react/3x2/CY";
import LB from "country-flag-icons/react/3x2/LB";
import GG from "country-flag-icons/react/3x2/GG";
import KE from "country-flag-icons/react/3x2/KE";
import JO from "country-flag-icons/react/3x2/JO";
import JE from "country-flag-icons/react/3x2/JE";
import HU from "country-flag-icons/react/3x2/HU";

const FLAGS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  AE, AR, AT, AU, BE, BR, CA, CH, CL, CN, CO, CZ, DE, DK, ES, FI, FR, GB,
  GR, HK, ID, IE, IL, IN, IT, JP, KR, KW, MX, MY, NL, NO, NZ, PH, PL, PT,
  QA, RU, SA, SE, SG, TH, TR, TW, UA, US, VN, ZA,
  BM, LU, KY, KZ, PE, SI, MA, CY, LB, GG, KE, JO, JE, HU,
};

const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

function codeToEmoji(code: string): string {
  const c = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return "";
  const A = 0x1f1e6;
  return (
    String.fromCodePoint(A + (c.charCodeAt(0) - 65)) +
    String.fromCodePoint(A + (c.charCodeAt(1) - 65))
  );
}

let _isWindows: boolean | null = null;
function isWindows(): boolean {
  if (_isWindows !== null) return _isWindows;
  if (typeof navigator === "undefined") return false;
  _isWindows = /Win/i.test(navigator.platform || "") || /Windows/i.test(navigator.userAgent || "");
  return _isWindows;
}

interface CountryFlagProps {
  country: string;
  size?: number;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Renders a country flag that looks great on every OS:
 * - Apple/Linux: native emoji flags (beautiful Apple-style on Mac/iOS)
 * - Windows: SVG flags from country-flag-icons (since Windows doesn't render flag emoji)
 */
export default function CountryFlag({ country, size = 14, style, className }: CountryFlagProps) {
  const code = countryNameToCode(country) || country.trim().toUpperCase();
  const useEmoji = useMemo(() => !isWindows(), []);

  if (useEmoji) {
    const emoji = codeToEmoji(code);
    if (!emoji) return null;
    return (
      <span
        className={className}
        style={{
          fontSize: size,
          lineHeight: 1,
          fontFamily: EMOJI_FONT,
          flexShrink: 0,
          ...style,
        }}
      >
        {emoji}
      </span>
    );
  }

  const Flag = FLAGS[code];
  if (!Flag) return null;
  const width = Math.round(size * 1.5);

  return (
    <Flag
      className={className}
      style={{
        width,
        height: size,
        borderRadius: Math.max(1, size * 0.12),
        flexShrink: 0,
        display: "inline-block",
        verticalAlign: "middle",
        ...style,
      }}
    />
  );
}
